<template>
  <div class="skills-session stack tight">
    <UiPanel tone="work">
      <div class="header">
        <div>
          <h3 class="title">Skills</h3>
          <div class="subtitle text-muted">Progress, levels, and modifiers.</div>
        </div>
        <UiChip v-if="lastUsedSkillLabel" variant="muted" :title="`Last used: ${lastUsedSkillLabel}`">
          <span>Recent</span>
          <span class="chip-strong">{{ lastUsedSkillLabel }}</span>
        </UiChip>
      </div>
    </UiPanel>

    <div class="list">
      <UiPanel v-for="skill in sortedSkills" :key="skill.id" class="row-card" tone="work">
        <div class="row-top">
          <div class="row-left">
            <div class="icon" aria-hidden="true">{{ skill.icon }}</div>
            <div class="row-meta">
              <div class="name">
                <span>{{ skill.label }}</span>
                <UiChip v-if="skill.id === lastUsedSkillId" variant="active">Active</UiChip>
              </div>
              <div class="sub text-muted">
                Lv <span class="stat-mono num">{{ formatNumber(skill.effectiveLevel) }}</span>
                <span v-if="skill.modifier !== 0" class="modifier">
                  (<span class="stat-mono num">{{ skill.modifier > 0 ? `+${formatNumber(skill.modifier)}` : formatNumber(skill.modifier) }}</span>)
                </span>
              </div>
            </div>
          </div>

          <div class="row-right">
            <div class="xp text-muted stat-mono num">
              {{ formatNumber(skill.xpInLevel) }} / {{ formatNumber(skill.xpToNext) }} XP
            </div>
            <div class="total stat-mono num">{{ formatNumber(skill.xp) }} total</div>
          </div>
        </div>

        <UiProgressBar :value="skill.progressPct" tone="work" />
      </UiPanel>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { SkillId, SkillProgress } from "@idle-rpg/shared";
import { combatXpForLevel, xpForLevel } from "@idle-rpg/shared";
import { formatNumber } from "../../../lib/format";
import { skillIcon, skillLabel } from "../../../lib/skillMeta";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiProgressBar from "../../../components/ui/UiProgressBar.vue";

const props = defineProps<{
  skills: SkillProgress[];
  lastUsedSkillId?: SkillId | null;
  skillModifiers?: Partial<Record<SkillId, number>> | null;
}>();

const lastUsedSkillLabel = computed(() => {
  if (!props.lastUsedSkillId) return null;
  return skillLabel(props.lastUsedSkillId);
});

const sortedSkills = computed(() =>
  [...props.skills]
    .map((s) => {
      const modifierRaw = Math.floor(Number((props.skillModifiers as any)?.[s.id] ?? 0));
      const modifier = Number.isFinite(modifierRaw) ? modifierRaw : 0;
      const effectiveLevel =
        s.id === "character" ? Math.max(1, Math.min(20, s.level + modifier)) : Math.max(0, Math.min(99, s.level + modifier));

      const isCharacter = s.id === "character";
      const maxLevel = isCharacter ? 20 : 99;
      const currentLevelXp = isCharacter ? combatXpForLevel(s.level) : xpForLevel(s.level);
      const nextLevelXp =
        s.level >= maxLevel
          ? isCharacter
            ? combatXpForLevel(maxLevel)
            : xpForLevel(maxLevel)
          : isCharacter
            ? combatXpForLevel(s.level + 1)
            : xpForLevel(s.level + 1);

      const xpInLevel = Math.max(0, s.xp - currentLevelXp);
      const xpToNext = Math.max(1, nextLevelXp - currentLevelXp);
      const progressPct = Math.min(100, Math.max(0, (xpInLevel / xpToNext) * 100));

      return {
        ...s,
        modifier,
        effectiveLevel,
        label: skillLabel(s.id),
        icon: skillIcon(s.id),
        xpInLevel,
        xpToNext,
        progressPct: Number(progressPct.toFixed(1))
      };
    })
    .sort((a, b) => {
      if (b.xp !== a.xp) return b.xp - a.xp;
      return a.label.localeCompare(b.label);
    })
);

</script>

<style scoped>
.skills-session {
  min-width: 0;
  container-type: inline-size;
}

.header {
  display: flex;
  justify-content: space-between;
  gap: var(--space-md);
  align-items: flex-start;
}

.title {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.subtitle {
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.chip-strong {
  font-weight: 700;
  letter-spacing: 0.02em;
}

.list {
  display: grid;
  gap: var(--space-md);
}

.row-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.row-top {
  display: flex;
  gap: var(--space-md);
  justify-content: space-between;
  align-items: flex-start;
}

.row-left {
  display: flex;
  gap: var(--space-sm);
  align-items: flex-start;
  min-width: 0;
  flex: 1 1 auto;
}

.row-meta {
  flex: 1 1 auto;
  min-width: 0;
}

.icon {
  width: 38px;
  height: 38px;
  aspect-ratio: 1 / 1;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--glass-1);
  border: 1px solid var(--glass-border-0);
  box-shadow: var(--shadow-0);
  font-size: 1.1rem;
}

.name {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-weight: 700;
  letter-spacing: 0.01em;
  min-width: 0;
  flex-wrap: wrap;
}

.name > span {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  min-width: 0;
  flex: 1 1 auto;
}

.name :deep(.ui-chip) {
  flex: 0 0 auto;
}

.sub {
  margin-top: 0.2rem;
  font-size: 0.85rem;
  font-family: var(--font-mono);
}

.modifier {
  opacity: 0.9;
}

.row-right {
  text-align: right;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 0 0 auto;
  min-width: 0;
}

.xp {
  font-size: 0.8rem;
  font-family: var(--font-mono);
}

.total {
  font-size: 0.85rem;
  font-weight: 700;
  font-family: var(--font-mono);
}

@media (max-width: 720px) {
  .row-top {
    flex-direction: column;
  }
  .row-right {
    text-align: left;
  }
}

@container (max-width: 420px) {
  .row-top {
    flex-direction: column;
    align-items: flex-start;
  }

  .row-right {
    text-align: left;
    min-width: 0;
  }
}

@media (min-width: 960px) {
  .list {
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  }

  .row-card {
    height: 100%;
  }
}
</style>
